/**
 * MTO Auto Product Add — Horizon adaptation
 *
 * Keeps a configured gift line in sync with the merchant's cart conditions:
 * grants it when the conditions hold, withdraws it when they stop holding, and
 * repairs the line whenever the cart diverges from the intended state — a
 * shopper-edited quantity, duplicate gift lines, or a gift line that predates
 * the `_mto_gift` property the cart template needs in order to lock the row.
 *
 * Every mutation runs through one promise chain, so overlapping cart events can
 * never race into a double add. Each sync announces at most one
 * CartLinesUpdateEvent, with action 'update' so the cart drawer refreshes
 * without auto-opening.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/** Line item property that marks — and locks — the auto-granted gift line. */
const GIFT_PROPERTY = '_mto_gift';

if (!customElements.get('mto-autoadd-product')) {
  class MtoAutoaddProduct extends HTMLElement {
    /** @type {Promise<void>} Serialises every cart mutation this element makes. */
    #queue = Promise.resolve();

    /** @type {boolean} True while our own mutations are in flight. */
    #mutating = false;

    /** @type {boolean} Set when granting the gift fails, to stop endless retries. */
    #addBlocked = false;

    /** @type {(() => void) | undefined} */
    #onCartUpdate;

    connectedCallback() {
      // Deliberately runs even when the section is switched off: turning it off
      // must also withdraw a gift the shopper was already granted.
      this.#onCartUpdate = (event) => {
        if (this.#mutating) return;

        // Horizon announces cart changes optimistically — the event is dispatched
        // before the request that performs it has landed, so reading the cart now
        // would evaluate the state the shopper just left behind (the classic
        // "gift only shows up after a reload" symptom). The event carries a
        // promise that settles once the originating operation is done; wait for
        // it, then evaluate.
        const settled = typeof event?.promise?.then === 'function' ? event.promise : undefined;
        this.#enqueue(settled);
      };
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);

      // An empty cart has nothing to grant and nothing to clean up, so skip the
      // initial read and wait for the first cart event. The count is rendered
      // server-side; when it is missing we fall back to reading the cart.
      if (parseInt(this.dataset.cartItemCount ?? '', 10) !== 0) this.#enqueue();
    }

    disconnectedCallback() {
      if (this.#onCartUpdate) {
        document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
      }
    }

    /* ── Settings ── */
    get enabled() {
      return this.dataset.enabled === 'true';
    }
    get threshold() {
      return Math.round((parseFloat(this.dataset.threshold ?? '') || 0) * 100); // cents
    }
    get basis() {
      return this.dataset.basis || 'items_subtotal_price';
    }
    get minItems() {
      return parseInt(this.dataset.minItems ?? '', 10) || 0;
    }
    get logic() {
      return this.dataset.logic === 'or' ? 'or' : 'and';
    }
    get removeWhenFalse() {
      return this.dataset.removeWhenFalse === 'true';
    }
    get autoVariantId() {
      return parseInt(this.dataset.autoVariantId ?? '', 10) || 0;
    }
    get autoQuantity() {
      return Math.max(1, parseInt(this.dataset.autoQuantity ?? '', 10) || 1);
    }
    get requiredVariantId() {
      return parseInt(this.dataset.requiredVariantId ?? '', 10) || 0;
    }
    get giftLabel() {
      return this.dataset.giftLabel || 'Hediye';
    }

    /**
     * Appends a sync to the mutation chain so two cart events can never
     * evaluate the cart at the same time.
     * @param {Promise<unknown>} [settled] - Resolves once the cart operation
     *   that triggered this sync has actually landed.
     * @returns {Promise<void>}
     */
    #enqueue(settled) {
      this.#queue = this.#queue
        .then(async () => {
          // A failed cart operation still needs evaluating — the cart may have
          // changed in ways worth reacting to — so swallow its rejection.
          if (settled) await settled.catch(() => {});
          await this.#sync();
        })
        .catch((error) => {
          console.warn('[MTO-Autoadd]', error);
        });
      return this.#queue;
    }

    /**
     * Brings the cart to the intended state in a single pass.
     * @returns {Promise<void>}
     */
    async #sync() {
      const cart = await this.#fetchCart();
      const giftLines = cart.items.filter((item) => this.#isGiftLine(item));

      /** @type {Array<() => Promise<unknown>>} */
      const steps = [];

      if (!this.enabled) {
        for (const line of giftLines) steps.push((sections) => this.#changeLine(line.key, 0, sections));
        return this.#commit(steps);
      }

      if (!this.autoVariantId) return;

      if (!this.#conditionsMet(cart, giftLines)) {
        if (this.removeWhenFalse) {
          for (const line of giftLines) steps.push((sections) => this.#changeLine(line.key, 0, sections));
        }
        return this.#commit(steps);
      }

      // Conditions hold: the cart must end up with exactly one correct gift line.
      const keep = giftLines.find(
        (line) => line.variant_id === this.autoVariantId && this.#hasGiftProperty(line)
      );

      // Drop duplicates, wrong variants, and unmarked legacy lines. An unmarked
      // line is re-granted below with the property, which is what lets the cart
      // template lock the row.
      for (const line of giftLines) {
        if (line !== keep) steps.push((sections) => this.#changeLine(line.key, 0, sections));
      }

      if (!keep) {
        if (this.#addBlocked) return this.#commit(steps);
        steps.push((sections) => this.#addGift(sections));
      } else if (keep.quantity !== this.autoQuantity) {
        steps.push((sections) => this.#changeLine(keep.key, this.autoQuantity, sections));
      }

      return this.#commit(steps);
    }

    /**
     * Evaluates the merchant's conditions against the cart, with the gift's own
     * contribution stripped out — otherwise the gift would help satisfy the
     * rule that grants it and the threshold would latch.
     * @param {any} cart
     * @param {any[]} giftLines
     * @returns {boolean}
     */
    #conditionsMet(cart, giftLines) {
      let giftAmount = 0;
      let giftCount = 0;

      for (const line of giftLines) {
        giftAmount += line.final_line_price ?? line.line_price ?? 0;
        giftCount += line.quantity ?? 0;
      }

      const rawAmount = (this.basis in cart ? cart[this.basis] : cart.items_subtotal_price) || 0;
      const amount = Math.max(0, rawAmount - giftAmount);
      const itemCount = Math.max(0, (cart.item_count || 0) - giftCount);

      const checks = [
        this.threshold === 0 || amount >= this.threshold,
        this.minItems === 0 || itemCount >= this.minItems,
        !this.requiredVariantId ||
          cart.items.some(
            (item) => item.variant_id === this.requiredVariantId && !this.#isGiftLine(item)
          ),
      ];

      return this.logic === 'or' ? checks.some(Boolean) : checks.every(Boolean);
    }

    /**
     * The property is the source of truth. The variant match is the repair path
     * for lines added before the property existed — the gift product is a
     * campaign giveaway with no standalone purchase route, so a variant match
     * without the property is a stale gift, not a shopper's own purchase.
     * @param {any} item
     * @returns {boolean}
     */
    #isGiftLine(item) {
      if (this.#hasGiftProperty(item)) return true;
      return this.autoVariantId !== 0 && item.variant_id === this.autoVariantId;
    }

    /**
     * @param {any} item
     * @returns {boolean}
     */
    #hasGiftProperty(item) {
      const value = item.properties?.[GIFT_PROPERTY];
      return typeof value === 'string' && value.length > 0;
    }

    /**
     * Runs the queued mutations, then refreshes the cart surfaces once.
     * @param {Array<(sections?: string) => Promise<any>>} steps
     * @returns {Promise<void>}
     */
    async #commit(steps) {
      if (!steps.length) return;

      this.#mutating = true;
      const deferred = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update', // never 'add' — the drawer must not pop open on its own
          context: 'cart',
          lines: [{ merchandiseId: String(this.autoVariantId), quantity: this.autoQuantity }],
          promise: deferred.promise,
        })
      );

      try {
        // Only the final mutation asks for the re-rendered sections, so the
        // shopper waits on one round trip instead of a separate render request.
        const sectionIds = this.#sectionIds();
        let last;
        for (let i = 0; i < steps.length; i++) {
          last = await steps[i](i === steps.length - 1 ? sectionIds : undefined);
        }

        // /cart/change.js answers with the whole cart; /cart/add.js only with the
        // lines it added, so read the cart back when we can't reuse the response.
        const cart = typeof last?.item_count === 'number' ? last : await this.#fetchCart();
        const sections = last?.sections ?? (await this.#fetchSections(sectionIds));

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'mto-autoadd-product',
            sourceId: 'mto-autoadd-product',
            itemCount: cart.item_count,
            sections,
            didError: false,
          },
        });
      } catch (error) {
        deferred.reject(error instanceof Error ? error : new Error(String(error)));
        throw error;
      } finally {
        this.#mutating = false;
      }
    }

    /**
     * @returns {Promise<any>}
     */
    async #fetchCart() {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
      return await response.json();
    }

    /**
     * The cart sections currently on the page, as the comma-separated list the
     * cart AJAX API expects.
     * @returns {string | undefined}
     */
    #sectionIds() {
      /** @type {Set<string>} */
      const ids = new Set();
      for (const element of document.querySelectorAll('cart-items-component')) {
        if (element instanceof HTMLElement && element.dataset.sectionId) {
          ids.add(element.dataset.sectionId);
        }
      }
      return ids.size ? [...ids].join(',') : undefined;
    }

    /**
     * Fallback re-render, for when the mutation response carried no sections.
     * @param {string} [sections]
     * @returns {Promise<Record<string, string> | undefined>}
     */
    async #fetchSections(sections) {
      if (!sections) return undefined;

      const url = new URL(Theme.routes.cart_url, window.location.origin);
      url.searchParams.set('sections', sections);

      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return undefined;
      return await response.json();
    }

    /**
     * @param {string} [sections] - Cart sections to re-render in the same request.
     * @returns {Promise<any>}
     */
    async #addGift(sections) {
      const data = await this.#post(Theme.routes.cart_add_url, {
        items: [
          {
            id: this.autoVariantId,
            quantity: this.autoQuantity,
            properties: { [GIFT_PROPERTY]: this.giftLabel },
          },
        ],
        ...(sections ? { sections } : {}),
      });

      if (data?.status) {
        // Out of stock, unpublished, quantity rule… retrying on every cart event
        // would hammer the endpoint, so stand down until the next page load.
        this.#addBlocked = true;
        throw new Error(data.description || data.message || 'Auto-add failed');
      }

      return data;
    }

    /**
     * @param {string} key - Cart line key.
     * @param {number} quantity - Target quantity; 0 removes the line.
     * @param {string} [sections] - Cart sections to re-render in the same request.
     * @returns {Promise<any>}
     */
    async #changeLine(key, quantity, sections) {
      const data = await this.#post(Theme.routes.cart_change_url, {
        id: key,
        quantity,
        ...(sections ? { sections } : {}),
      });
      if (data?.status) throw new Error(data.description || data.message || 'Cart change failed');
      return data;
    }

    /**
     * @param {string} url
     * @param {Record<string, unknown>} body
     * @returns {Promise<any>}
     */
    async #post(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      return await response.json();
    }
  }

  customElements.define('mto-autoadd-product', MtoAutoaddProduct);
}
